function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * initialize all the reducers and the store in this file.
 */
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import { createStore, combineReducers, applyMiddleware } from 'redux';
import { routerReducer } from 'react-router-redux';

// Safe to ignore: middleware is only instantiated at store creation time, and
// loginActions.idleTimer is only called at runtime
import idleTimerMiddleware from '../middleware/idleTimerMiddleware'; // eslint-disable-line import/no-cycle

import applyReducer from './applyReducer';
import tokenErrorReducer from './tokenErrorReducer';
import documentReducerFactory from './factories/documentReducerFactory';

// get all reducers
import reportAnnotationReducer from './reportAnnotationReducer';
import annotationReducer from './annotationReducer';
import annotationSelectionReducer from './annotationSelectionReducer';
import assetReducer from './assetReducer';
import cabinetReducer from './cabinetReducer';
import cabinetFilterOptionsReducer from './cabinetFilterOptionsReducer';
import configBehaviorReducer from './configBehaviorReducer';
import contractSpecReducer from './contractSpecReducer';
import deferredReducer from './deferredReducer';
import documentTagSelectDocumentReducer from './documentTagSelectDocumentReducer';
import employeeReducer from './employeeReducer';
import userReducer from './userReducer';
import formReducer from './formReducer';
import formTypeReducer from './formTypeReducer';
import issueReducer from './issueReducer';
import mapReducer from './mapReducer';
import reportFilterReducer from './reportFilterReducer';
import reportReferenceReducer from './reportReferenceReducer';
import reportIssueBulkExportReducer from './reportIssueBulkExportReducer';
import reportReducer from './reportReducer';
import infoReducer from './infoReducer';
import jobReducer from './jobReducer';
import loginReducer from './loginReducer';
import organizationReducer from './organizationReducer';
import passwordReducer from './passwordReducer';
import reportTagReducer from './reportTagReducer';
import notificationReducer from './notificationReducer';
import settingsReducer from './settingsReducer';
import summaryFilterOptionsReducer from './summaryFilterOptionsReducer';

// document filters
import assetFilterReportReducer from './assetFilterReportReducer';

// custom lookups
import defaultLookupReducers from './lookup';
import * as customLookupReducers from './lookup/customLookupReducers';
import * as dependentFilterReducers from './lookup/dependentFilterReducers';
import fillReportSelectEditorReducer from './fillReportSelectEditorReducer';
import fillReportSelectJobReducer from './fillReportSelectJobReducer';
import formModalReducer from './formModalReducer';

// special
import dashboardReducer from './dashboardReducer';
import schemaReducer from './schemaReducer';
import mfaReducer from './mfaReducer';
import reportDocumentReducer from './reportDocumentReducer';
import pickerReducer from './pickerReducer';
const documentReducer = documentReducerFactory('document');
const document = applyReducer(documentReducer.reducers, documentReducer.initState);
const documentSelectJobReducer = documentReducerFactory('documentSelectJob');
const documentSelectJob = applyReducer(documentSelectJobReducer.reducers, documentSelectJobReducer.initState);
const documentModalJobReducer = documentReducerFactory('documentModalJob');
const documentModalJob = applyReducer(documentModalJobReducer.reducers, documentModalJobReducer.initState);

// -----------------------------
// define redux framework
const middleware = [thunk, idleTimerMiddleware];
const reducers = _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, defaultLookupReducers), customLookupReducers), dependentFilterReducers), {}, {
  annotation: annotationReducer,
  annotationSelection: annotationSelectionReducer,
  asset: assetReducer,
  assetFilterReport: assetFilterReportReducer,
  // report filter
  cabinet: cabinetReducer,
  cabinetFilterOptions: cabinetFilterOptionsReducer,
  configBehavior: configBehaviorReducer,
  contractSpec: contractSpecReducer,
  dashboard: dashboardReducer,
  deferred: deferredReducer,
  document,
  documentTagSelectDocument: documentTagSelectDocumentReducer,
  documentSelectJob,
  // job detail
  documentModalJob,
  // reportEditor
  fillReportSelectEditor: fillReportSelectEditorReducer,
  fillReportSelectJob: fillReportSelectJobReducer,
  form: formReducer,
  formModal: formModalReducer,
  formType: formTypeReducer,
  info: infoReducer,
  issue: issueReducer,
  job: jobReducer,
  lastAction: (state, action) => {
    // Actions that should NOT reset the idle timer
    // These are system/internal actions that don't represent user activity
    const IDLE_TIMER_IGNORE_ACTIONS = ['DISMISS',
    // Notification dismiss shouldn't reset timer
    'LOGOUT',
    // Logout action itself shouldn't reset timer
    'AUTO_LOGOUT_WITH_SAVE' // Auto-logout related actions
    ];

    // Don't update timestamp for actions that shouldn't reset the idle timer
    // This prevents the timer from being reset when system actions are dispatched
    // Note: KEEP_ALIVE is intentionally NOT in this list - it should reset the timer
    if (IDLE_TIMER_IGNORE_ACTIONS.includes(action.type)) {
      // Keep the previous timestamp if state exists, otherwise create new one
      return state ? _objectSpread(_objectSpread({}, state), {}, {
        action
      }) : {
        action,
        timestamp: Date.now()
      };
    }
    return {
      action,
      timestamp: Date.now()
    };
  },
  login: loginReducer,
  map: mapReducer,
  organization: organizationReducer,
  password: passwordReducer,
  picker: pickerReducer,
  reportAnnotation: reportAnnotationReducer,
  reportFilter: reportFilterReducer,
  reportTag: reportTagReducer,
  report: reportReducer,
  reportReference: reportReferenceReducer,
  reportIssueBulkExport: reportIssueBulkExportReducer,
  schema: schemaReducer,
  summaryFilterOptions: summaryFilterOptionsReducer,
  settings: settingsReducer,
  tokenError: tokenErrorReducer,
  mfa: mfaReducer,
  employee: employeeReducer,
  reportDocument: reportDocumentReducer,
  notification: notificationReducer,
  user: userReducer
});

// combine reducers and create the global redux store.
// include middle ware - in this case [thunk], which allows
// us to make ajax calls as part of our actions.
const reducer = combineReducers(_objectSpread(_objectSpread({}, reducers), {}, {
  routing: routerReducer
}));

// adding a `root` reducer so that we can wrap the combined reducers
// wrapping the combined reducers allowes us to clear the state of all reducers
// with one simple action - needed for user logout so we don't get data
// contamination between users or customers
const rootReducer = (state, action) => {
  // console.log('action called:', action.type);
  // This will log out any redux action (including redux init) if uncommented
  // console.log('actions:', action.type);
  let newState = state;
  if (action.type === 'LOGOUT') {
    // console.log('logout is being called');
    newState = undefined;
  }
  return reducer(newState, action);
};
const store = createStore(rootReducer, composeWithDevTools(applyMiddleware(...middleware)));
export default store;